import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  useTheme,
  Grid,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Chip,
  CircularProgress,
  Slider,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingFlat as TrendingFlatIcon,
  CheckCircle as HealthyIcon,
  Warning as WarningIcon,
  Error as BreachedIcon,
  HelpOutline as NoDataIcon,
  Psychology as AIIcon,
  Timer as TimerIcon,
  ThumbDown as FPIcon,
  Savings as BudgetIcon,
  Save as SaveIcon,
  Check as CheckIcon,
  FileDownload as ExportIcon,
  Speed as MttdIcon,
  Dataset as DataQualityIcon,
  Memory as AgentIcon,
  CompareArrows as BaselineIcon,
  Gavel as AcknowledgeIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import {
  RootState, AIAccuracyMetrics, AIMetricsSnapshot, MTTRMetrics, ErrorBudgetStatus,
  PlaybookTimelineStats, MTTDMetrics, DataQualityMetrics, AgentUptimeStats,
  BaselineComparison, ErrorBudgetAcknowledgment,
} from '../types';
import { fetchClients } from '../store/slices/clientsSlice';
import { AIMetricsService } from '../services/aiMetricsService';
import { PlaybooksService } from '../services/playbooksService';
import { LineChart } from '../components/graphics/LineChart';
import { colorUtils } from '../theme';

// ─── Shared helpers ──────────────────────────────────────────────────────────

type Trend = 'improving' | 'worsening' | 'stable';

const TrendBadge: React.FC<{ trend: Trend }> = ({ trend }) => {
  const theme = useTheme();
  const cfg: Record<Trend, { label: string; color: string; icon: React.ReactElement }> = {
    improving: {
      label: 'Improving',
      color: theme.palette.success.main,
      icon: <TrendingUpIcon sx={{ fontSize: 11 }} />,
    },
    worsening: {
      label: 'Worsening',
      color: theme.palette.error.main,
      icon: <TrendingUpIcon sx={{ fontSize: 11, transform: 'rotate(90deg)' }} />,
    },
    stable: {
      label: 'Stable',
      color: theme.palette.text.secondary,
      icon: <TrendingFlatIcon sx={{ fontSize: 11 }} />,
    },
  };
  const { label, color, icon } = cfg[trend];
  return (
    <Chip
      icon={icon}
      label={label}
      size="small"
      sx={{
        height: 20,
        fontSize: '0.62rem',
        fontWeight: 700,
        color,
        backgroundColor: colorUtils.withOpacity(color, 0.1),
        border: `1px solid ${colorUtils.withOpacity(color, 0.3)}`,
        '& .MuiChip-icon': { color, ml: 0.75 },
      }}
    />
  );
};

const ErrorBudgetBadge: React.FC<{ status: ErrorBudgetStatus['status'] }> = ({ status }) => {
  const theme = useTheme();
  const cfg: Record<ErrorBudgetStatus['status'], { label: string; color: string; icon: React.ReactElement }> = {
    healthy:  { label: 'Healthy',  color: theme.palette.success.main,  icon: <HealthyIcon sx={{ fontSize: 13 }} /> },
    warning:  { label: 'Warning',  color: theme.palette.warning.main,  icon: <WarningIcon sx={{ fontSize: 13 }} /> },
    breached: { label: 'Breached', color: theme.palette.error.main,    icon: <BreachedIcon sx={{ fontSize: 13 }} /> },
    no_data:  { label: 'No Data',  color: theme.palette.text.disabled, icon: <NoDataIcon sx={{ fontSize: 13 }} /> },
  };
  const { label, color, icon } = cfg[status];
  return (
    <Chip
      icon={icon}
      label={label}
      size="small"
      sx={{
        height: 22,
        fontSize: '0.7rem',
        fontWeight: 700,
        color,
        backgroundColor: colorUtils.withOpacity(color, 0.12),
        border: `1px solid ${colorUtils.withOpacity(color, 0.4)}`,
        '& .MuiChip-icon': { color, ml: 0.75 },
      }}
    />
  );
};

// ─── Page ────────────────────────────────────────────────────────────────────

const AIPerformancePage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { clients } = useSelector((state: RootState) => state.clients);

  const [clientId, setClientId] = useState('');
  const [days, setDays] = useState<90 | 30 | 7>(90);

  const [accuracy, setAccuracy] = useState<AIAccuracyMetrics | null>(null);
  const [accuracyLoading, setAccuracyLoading] = useState(false);

  const [mttr, setMTTR] = useState<MTTRMetrics | null>(null);
  const [mttrLoading, setMTTRLoading] = useState(false);

  const [errorBudget, setErrorBudget] = useState<ErrorBudgetStatus | null>(null);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [thresholdDraft, setThresholdDraft] = useState<number>(70);
  const [thresholdSaving, setThresholdSaving] = useState(false);
  const [thresholdSaved, setThresholdSaved] = useState(false);

  const [fpTimeline, setFpTimeline] = useState<PlaybookTimelineStats | null>(null);
  const [fpLoading, setFpLoading] = useState(false);

  const [snapshots, setSnapshots] = useState<AIMetricsSnapshot[]>([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [mttd, setMTTD] = useState<MTTDMetrics | null>(null);
  const [mttdLoading, setMTTDLoading] = useState(false);

  const [dataQuality, setDataQuality] = useState<DataQualityMetrics | null>(null);
  const [dataQualityLoading, setDataQualityLoading] = useState(false);

  const [agentUptime, setAgentUptime] = useState<AgentUptimeStats | null>(null);
  const [agentUptimeLoading, setAgentUptimeLoading] = useState(false);

  const [baseline, setBaseline] = useState<BaselineComparison | null>(null);
  const [baselineLoading, setBaselineLoading] = useState(false);

  const [cisoAck, setCisoAck] = useState<ErrorBudgetAcknowledgment | null>(null);

  // Baseline declare dialog
  const [baselineDialogOpen, setBaselineDialogOpen] = useState(false);
  const [baselineStart, setBaselineStart] = useState('');
  const [baselineEnd, setBaselineEnd] = useState('');
  const [baselineManual, setBaselineManual] = useState(false);
  const [baselineManualMttr, setBaselineManualMttr] = useState('');
  const [baselineManualMttd, setBaselineManualMttd] = useState('');
  const [baselineSaving, setBaselineSaving] = useState(false);

  // CISO acknowledge dialog
  const [cisoDialogOpen, setCisoDialogOpen] = useState(false);
  const [cisoNote, setCisoNote] = useState('');
  const [cisoSaving, setCisoSaving] = useState(false);

  useEffect(() => {
    if (clients.length === 0) dispatch(fetchClients() as any);
  }, []);

  const period = days === 7 ? '7d' : days === 30 ? '30d' : '90d';

  const loadAll = (cid: string) => {
    setAccuracyLoading(true);
    AIMetricsService.getAIAccuracy(cid, days)
      .then(setAccuracy)
      .catch(() => setAccuracy(null))
      .finally(() => setAccuracyLoading(false));

    setMTTRLoading(true);
    AIMetricsService.getMTTR(cid, days)
      .then(setMTTR)
      .catch(() => setMTTR(null))
      .finally(() => setMTTRLoading(false));

    setBudgetLoading(true);
    AIMetricsService.getErrorBudget(cid)
      .then((data) => {
        setErrorBudget(data);
        setThresholdDraft(data.threshold);
      })
      .catch(() => setErrorBudget(null))
      .finally(() => setBudgetLoading(false));

    setFpLoading(true);
    PlaybooksService.getTimelineStats(cid, period)
      .then(setFpTimeline)
      .catch(() => setFpTimeline(null))
      .finally(() => setFpLoading(false));

    setSnapshotsLoading(true);
    AIMetricsService.getSnapshots(cid)
      .then((data) => setSnapshots([...data].reverse()))
      .catch(() => setSnapshots([]))
      .finally(() => setSnapshotsLoading(false));

    setMTTDLoading(true);
    AIMetricsService.getMTTD(cid, days)
      .then(setMTTD)
      .catch(() => setMTTD(null))
      .finally(() => setMTTDLoading(false));

    setDataQualityLoading(true);
    AIMetricsService.getDataQuality(cid, days)
      .then(setDataQuality)
      .catch(() => setDataQuality(null))
      .finally(() => setDataQualityLoading(false));

    setAgentUptimeLoading(true);
    AIMetricsService.getAgentUptime()
      .then(setAgentUptime)
      .catch(() => setAgentUptime(null))
      .finally(() => setAgentUptimeLoading(false));

    setBaselineLoading(true);
    AIMetricsService.getBaselineComparison(cid)
      .then(setBaseline)
      .catch(() => setBaseline(null))
      .finally(() => setBaselineLoading(false));

    AIMetricsService.getErrorBudgetAcknowledgment(cid)
      .then(setCisoAck)
      .catch(() => setCisoAck(null));
  };

  useEffect(() => {
    if (clientId) loadAll(clientId);
    else {
      setAccuracy(null);
      setMTTR(null);
      setErrorBudget(null);
      setFpTimeline(null);
      setSnapshots([]);
      setMTTD(null);
      setDataQuality(null);
      setAgentUptime(null);
      setBaseline(null);
      setCisoAck(null);
    }
  }, [clientId, days]);

  const handleSaveThreshold = async () => {
    if (!clientId) return;
    setThresholdSaving(true);
    try {
      await AIMetricsService.saveErrorBudgetThreshold(clientId, thresholdDraft);
      setErrorBudget((prev) => prev ? { ...prev, threshold: thresholdDraft } : prev);
      setThresholdSaved(true);
      setTimeout(() => setThresholdSaved(false), 2500);
    } finally {
      setThresholdSaving(false);
    }
  };

  const handleDeclareBaseline = async () => {
    if (!clientId || !baselineStart || !baselineEnd) return;
    setBaselineSaving(true);
    try {
      const payload: any = { client_id: clientId, period_start: baselineStart, period_end: baselineEnd };
      if (baselineManual) {
        payload.metrics = {
          avg_mttr_hours: baselineManualMttr ? parseFloat(baselineManualMttr) : null,
          avg_mttd_hours: baselineManualMttd ? parseFloat(baselineManualMttd) : null,
        };
      }
      await AIMetricsService.declareBaseline(payload);
      setBaselineDialogOpen(false);
      setBaselineStart(''); setBaselineEnd(''); setBaselineManualMttr(''); setBaselineManualMttd(''); setBaselineManual(false);
      if (clientId) {
        setBaselineLoading(true);
        AIMetricsService.getBaselineComparison(clientId).then(setBaseline).catch(() => setBaseline(null)).finally(() => setBaselineLoading(false));
      }
    } finally {
      setBaselineSaving(false);
    }
  };

  const handleCISOAcknowledge = async () => {
    if (!clientId) return;
    setCisoSaving(true);
    try {
      const ack = await AIMetricsService.acknowledgeErrorBudget(clientId, cisoNote || undefined);
      setCisoAck(ack);
      setCisoDialogOpen(false);
      setCisoNote('');
    } finally {
      setCisoSaving(false);
    }
  };

  // ─── Shared styles ──────────────────────────────────────────────────────
  const cardSx = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 1.5,
    p: 2.5,
    height: '100%',
  };

  const SectionLabel: React.FC<{ children: React.ReactNode; sx?: object }> = ({ children, sx: sxOverride }) => (
    <Typography
      variant="caption"
      sx={{
        color: theme.palette.text.tertiary,
        fontWeight: 700,
        display: 'block',
        mb: 1.5,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        fontSize: '0.65rem',
        ...sxOverride,
      }}
    >
      {children}
    </Typography>
  );

  const BigNumber: React.FC<{ value: number | null; unit?: string; color?: string }> = ({
    value,
    unit = '%',
    color,
  }) => (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
      <Typography
        sx={{
          fontSize: '2.4rem',
          fontWeight: 700,
          lineHeight: 1,
          color: color ?? theme.palette.text.primary,
          fontFamily: 'monospace',
        }}
      >
        {value !== null ? value : '—'}
      </Typography>
      {value !== null && (
        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: theme.palette.text.secondary }}>
          {unit}
        </Typography>
      )}
    </Box>
  );

  // ─── Derived chart data ─────────────────────────────────────────────────

  const accuracyChartData = (accuracy?.weekly_trend ?? []).map((b) => ({
    name: b.week.slice(5), // MM-DD
    'Acceptance Rate': b.acceptance_rate ?? 0,
  }));

  const mttrChartData = (mttr?.weekly_trend ?? []).map((b) => ({
    name: b.week.slice(5),
    'AI-Assisted': b.ai_assisted_avg_hours ?? 0,
    'Manual': b.manual_avg_hours ?? 0,
  }));

  const fpChartData = (fpTimeline?.buckets ?? []).map((b) => ({
    name: b.week.slice(5),
    'FP Rate %': fpTimeline
      ? (() => {
          const decided = b.handled + b.escalated + b.false_positive;
          return decided > 0 ? Math.round((b.false_positive / decided) * 100 * 10) / 10 : 0;
        })()
      : 0,
  }));

  const incidentTypeData = (accuracy?.by_incident_type ?? []).map((t) => ({
    name: t.incident_type,
    'Acceptance Rate': t.acceptance_rate ?? 0,
  }));

  const budgetStatusColor: Record<ErrorBudgetStatus['status'], string> = {
    healthy: theme.palette.success.main,
    warning: theme.palette.warning.main,
    breached: theme.palette.error.main,
    no_data: theme.palette.text.disabled,
  };

  const noClientSelected = !clientId;

  // ─── Placeholder card ────────────────────────────────────────────────────

  const EmptyCard: React.FC<{ loading?: boolean }> = ({ loading }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 120,
      }}
    >
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Typography variant="body2" sx={{ color: theme.palette.error.light }}>
          {noClientSelected ? 'Select a client to view data' : 'No data available'}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.default,
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        px: { xs: 2, sm: 3, md: 4 },
        pt: 10, pb: 3,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: '100%' }}>

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 2, sm: 0 },
            mb: 4,
          }}
        >
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <AIIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
              <Typography variant="h1" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                AI Performance
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
              Track AI accuracy, analyst override rate, MTTR impact, and error budget health
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Client selector */}
            <FormControl size="small">
              <Select
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                displayEmpty
                sx={{
                  height: 40,
                  fontSize: '0.8rem',
                  backgroundColor: theme.palette.background.tertiary,
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  minWidth: 180,
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&:hover': { backgroundColor: theme.palette.background.secondary },
                }}
              >
                <MenuItem value="" sx={{ fontSize: '0.8rem', color: theme.palette.text.tertiary }}>
                  Select client…
                </MenuItem>
                {clients.map((c) => (
                  <MenuItem key={c.id} value={c.id} sx={{ fontSize: '0.8rem' }}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Period selector */}
            <FormControl size="small">
              <Select
                value={days}
                onChange={(e) => setDays(e.target.value as 7 | 30 | 90)}
                sx={{
                  height: 40,
                  fontSize: '0.8rem',
                  backgroundColor: theme.palette.background.tertiary,
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  minWidth: 120,
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&:hover': { backgroundColor: theme.palette.background.secondary },
                }}
              >
                <MenuItem value={7}  sx={{ fontSize: '0.8rem' }}>Last 7 days</MenuItem>
                <MenuItem value={30} sx={{ fontSize: '0.8rem' }}>Last 30 days</MenuItem>
                <MenuItem value={90} sx={{ fontSize: '0.8rem' }}>Last 90 days</MenuItem>
              </Select>
            </FormControl>

            <Tooltip title="Export training data (JSONL)">
              <span>
                <Button
                  disabled={!clientId || exportLoading}
                  variant="outlined"
                  size="small"
                  startIcon={exportLoading ? <CircularProgress size={13} color="inherit" /> : <ExportIcon sx={{ fontSize: 16 }} />}
                  onClick={async () => {
                    if (!clientId) return;
                    setExportLoading(true);
                    try {
                      await AIMetricsService.downloadTrainingData(clientId);
                    } finally {
                      setExportLoading(false);
                    }
                  }}
                  sx={{
                    height: 40,
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    borderColor: theme.palette.border.main,
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      backgroundColor: colorUtils.withOpacity(theme.palette.primary.main, 0.06),
                    },
                  }}
                >
                  Export
                </Button>
              </span>
            </Tooltip>

            <Tooltip title="Refresh">
              <span>
                <IconButton
                  disabled={!clientId}
                  onClick={() => clientId && loadAll(clientId)}
                  sx={{
                    bgcolor: theme.palette.background.tertiary,
                    color: theme.palette.info.main,
                    borderRadius: 1,
                    width: 40,
                    height: 40,
                    '&:hover': {
                      bgcolor: theme.palette.info.main,
                      color: theme.palette.background.tertiary,
                      transform: 'rotate(180deg)',
                    },
                    transition: 'all 0.4s ease',
                  }}
                >
                  <RefreshIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* ── KPI Row ──────────────────────────────────────────────────────── */}
        <Grid container spacing={2} sx={{ mb: 3 }}>

          {/* Acceptance Rate KPI */}
          <Grid size={{ xs: 6, sm: 3 }}>
            <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <AIIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                <SectionLabel sx={{ mb: 0 }}>Acceptance Rate</SectionLabel>
              </Box>
              {accuracyLoading ? (
                <CircularProgress size={20} />
              ) : (
                <>
                  <BigNumber
                    value={accuracy?.overall_acceptance_rate ?? null}
                    color={
                      accuracy?.overall_acceptance_rate != null
                        ? accuracy.overall_acceptance_rate >= 70
                          ? theme.palette.success.main
                          : accuracy.overall_acceptance_rate >= 50
                          ? theme.palette.warning.main
                          : theme.palette.error.main
                        : undefined
                    }
                  />
                  {accuracy?.trend && <TrendBadge trend={accuracy.trend} />}
                  <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                    {accuracy?.total_decisions ?? 0} decisions recorded
                  </Typography>
                </>
              )}
            </Box>
          </Grid>

          {/* False Positive Rate KPI */}
          <Grid size={{ xs: 6, sm: 3 }}>
            <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <FPIcon sx={{ fontSize: 14, color: theme.palette.error.main }} />
                <SectionLabel sx={{ mb: 0 }}>False Positive Rate</SectionLabel>
              </Box>
              {fpLoading ? (
                <CircularProgress size={20} />
              ) : (
                <>
                  <BigNumber
                    value={fpTimeline?.false_positive_rate_pct ?? null}
                    color={
                      fpTimeline?.false_positive_rate_pct != null
                        ? fpTimeline.false_positive_rate_pct <= 10
                          ? theme.palette.success.main
                          : fpTimeline.false_positive_rate_pct <= 25
                          ? theme.palette.warning.main
                          : theme.palette.error.main
                        : undefined
                    }
                  />
                  {fpTimeline?.trend && <TrendBadge trend={fpTimeline.trend} />}
                  <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                    of decided recommendations
                  </Typography>
                </>
              )}
            </Box>
          </Grid>

          {/* MTTR AI-Assisted KPI */}
          <Grid size={{ xs: 6, sm: 3 }}>
            <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <TimerIcon sx={{ fontSize: 14, color: theme.palette.info.main }} />
                <SectionLabel sx={{ mb: 0 }}>MTTR (AI-Assisted)</SectionLabel>
              </Box>
              {mttrLoading ? (
                <CircularProgress size={20} />
              ) : (
                <>
                  <BigNumber
                    value={mttr?.ai_assisted_avg_hours ?? null}
                    unit="h"
                    color={theme.palette.success.main}
                  />
                  {mttr?.manual_avg_hours != null && (
                    <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                      vs {mttr.manual_avg_hours}h manual
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                    {mttr?.ai_assisted_count ?? 0} AI-assisted tickets
                  </Typography>
                </>
              )}
            </Box>
          </Grid>

          {/* Error Budget KPI */}
          <Grid size={{ xs: 6, sm: 3 }}>
            <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <BudgetIcon sx={{ fontSize: 14, color: theme.palette.warning.main }} />
                <SectionLabel sx={{ mb: 0 }}>Error Budget</SectionLabel>
              </Box>
              {budgetLoading ? (
                <CircularProgress size={20} />
              ) : errorBudget ? (
                <>
                  <ErrorBudgetBadge status={errorBudget.status} />
                  {errorBudget.current_accuracy != null ? (
                    <BigNumber value={errorBudget.current_accuracy} color={budgetStatusColor[errorBudget.status]} />
                  ) : (
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: theme.palette.text.tertiary, fontFamily: 'monospace' }}>
                      No data
                    </Typography>
                  )}
                  {errorBudget.trend && <TrendBadge trend={errorBudget.trend} />}
                  <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                    accuracy — must stay ≥ {errorBudget.threshold}% target
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                  {noClientSelected ? 'Select a client' : 'No data'}
                </Typography>
              )}
            </Box>
          </Grid>

          {/* MTTD KPI */}
          <Grid size={{ xs: 6, sm: 3 }}>
            <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <MttdIcon sx={{ fontSize: 14, color: theme.palette.primary.main }} />
                <SectionLabel sx={{ mb: 0 }}>MTTD (overall)</SectionLabel>
              </Box>
              {mttdLoading ? (
                <CircularProgress size={20} />
              ) : (
                <>
                  <BigNumber
                    value={mttd?.overall_avg_hours ?? null}
                    unit="h"
                    color={theme.palette.primary.main}
                  />
                  {mttd?.auto_ticketed_avg_hours != null && mttd?.manual_ticketed_avg_hours != null && (
                    <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                      Auto {mttd.auto_ticketed_avg_hours}h · Manual {mttd.manual_ticketed_avg_hours}h
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                    {mttd?.total_with_detection_time ?? 0} tickets with detection time
                  </Typography>
                </>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* ── Charts Row 1: Acceptance Rate + MTTR ────────────────────────── */}
        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>

          {/* Acceptance Rate Trend */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={cardSx}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <SectionLabel>AI Acceptance Rate — Weekly Trend</SectionLabel>
                  <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                    % of AI-suggested actions accepted by analysts
                  </Typography>
                </Box>
                {accuracy?.trend && <TrendBadge trend={accuracy.trend} />}
              </Box>
              {accuracyLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : accuracyChartData.length > 0 ? (
                <LineChart
                  data={accuracyChartData}
                  height={200}
                  variant="area"
                  curved
                  showLegend={false}
                  dataKeys={[
                    {
                      key: 'Acceptance Rate',
                      label: 'Acceptance Rate (%)',
                      color: 'oklch(65% 0.18 150)',
                    },
                  ]}
                />
              ) : (
                <EmptyCard />
              )}
            </Box>
          </Grid>

          {/* MTTR: AI-Assisted vs Manual */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={cardSx}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <SectionLabel>MTTR — AI-Assisted vs Manual</SectionLabel>
                  <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                    Average hours to resolve, by week
                  </Typography>
                </Box>
                {mttr && (
                  <Chip
                    label={
                      mttr.ai_assisted_avg_hours != null && mttr.manual_avg_hours != null
                        ? `${Math.round(((mttr.manual_avg_hours - mttr.ai_assisted_avg_hours) / mttr.manual_avg_hours) * 100)}% faster`
                        : 'No comparison yet'
                    }
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      color: theme.palette.success.main,
                      backgroundColor: colorUtils.withOpacity(theme.palette.success.main, 0.1),
                      border: `1px solid ${colorUtils.withOpacity(theme.palette.success.main, 0.3)}`,
                    }}
                  />
                )}
              </Box>
              {mttrLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : mttrChartData.length > 0 ? (
                <LineChart
                  data={mttrChartData}
                  height={200}
                  variant="line"
                  curved
                  showLegend
                  dataKeys={[
                    { key: 'AI-Assisted', label: 'AI-Assisted (h)', color: 'oklch(65% 0.18 150)' },
                    { key: 'Manual', label: 'Manual (h)', color: 'oklch(62% 0.22 25)' },
                  ]}
                />
              ) : (
                <EmptyCard />
              )}
            </Box>
          </Grid>
        </Grid>

        {/* ── Charts Row 2: FP Trend + Error Budget ───────────────────────── */}
        <Grid container spacing={2.5}>

          {/* False Positive Trend */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={cardSx}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <SectionLabel>False Positive Rate — Weekly Trend</SectionLabel>
                  <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                    % of decided recommendations marked as false positive
                  </Typography>
                </Box>
                {fpTimeline?.trend && <TrendBadge trend={fpTimeline.trend} />}
              </Box>
              {fpLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : fpChartData.length > 0 ? (
                <LineChart
                  data={fpChartData}
                  height={200}
                  variant="area"
                  curved
                  showLegend={false}
                  dataKeys={[
                    {
                      key: 'FP Rate %',
                      label: 'False Positive Rate (%)',
                      color: 'oklch(62% 0.22 25)',
                    },
                  ]}
                />
              ) : (
                <EmptyCard />
              )}

              {/* Breakdown by incident type */}
              {incidentTypeData.length > 0 && (
                <Box sx={{ mt: 2.5 }}>
                  <SectionLabel>Acceptance Rate by Incident Type</SectionLabel>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {incidentTypeData.map((row) => (
                      <Box key={row.name}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, textTransform: 'capitalize' }}
                          >
                            {row.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: theme.palette.text.primary, fontFamily: 'monospace' }}
                          >
                            {row['Acceptance Rate']}%
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            height: 5,
                            borderRadius: 3,
                            backgroundColor: theme.palette.background.tertiary,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              width: `${row['Acceptance Rate']}%`,
                              borderRadius: 3,
                              backgroundColor:
                                row['Acceptance Rate'] >= 70
                                  ? theme.palette.success.main
                                  : row['Acceptance Rate'] >= 50
                                  ? theme.palette.warning.main
                                  : theme.palette.error.main,
                              transition: 'width 0.4s ease',
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Error Budget Configuration */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box>
                <SectionLabel>AI Error Budget</SectionLabel>
                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                  Minimum acceptance rate threshold — breach triggers an analyst notification
                </Typography>
              </Box>

              {budgetLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : errorBudget ? (
                <>
                  {/* Status gauge */}
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1.5,
                      backgroundColor: colorUtils.withOpacity(
                        budgetStatusColor[errorBudget.status],
                        0.08,
                      ),
                      border: `1px solid ${colorUtils.withOpacity(budgetStatusColor[errorBudget.status], 0.25)}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <ErrorBudgetBadge status={errorBudget.status} />
                      <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.secondary, fontFamily: 'monospace' }}
                      >
                        {errorBudget.total_decisions} decisions (30d)
                      </Typography>
                    </Box>

                    {/* Fill bar */}
                    <Box
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        backgroundColor: theme.palette.background.tertiary,
                        overflow: 'visible',
                        position: 'relative',
                        mb: 0.75,
                      }}
                    >
                      {/* Threshold marker */}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${errorBudget.threshold}%`,
                          top: -4,
                          bottom: -4,
                          width: 2,
                          backgroundColor: theme.palette.text.tertiary,
                          borderRadius: 1,
                          zIndex: 2,
                        }}
                      />
                      {/* Current fill */}
                      <Box
                        sx={{
                          height: '100%',
                          width: `${Math.min(errorBudget.current_accuracy ?? 0, 100)}%`,
                          borderRadius: 5,
                          backgroundColor: budgetStatusColor[errorBudget.status],
                          transition: 'width 0.5s ease',
                        }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                        0%
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.tertiary, fontWeight: 600 }}
                      >
                        Target: {errorBudget.threshold}%
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                        100%
                      </Typography>
                    </Box>

                    <Typography
                      sx={{
                        mt: 1.5,
                        fontSize: '1.6rem',
                        fontWeight: 700,
                        fontFamily: 'monospace',
                        color: budgetStatusColor[errorBudget.status],
                        lineHeight: 1,
                      }}
                    >
                      {errorBudget.current_accuracy != null
                        ? `${errorBudget.current_accuracy}%`
                        : '—'}
                      <Typography
                        component="span"
                        sx={{ fontSize: '0.8rem', color: theme.palette.text.tertiary, ml: 0.75 }}
                      >
                        current accuracy
                      </Typography>
                    </Typography>
                  </Box>

                  {/* Threshold config */}
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Threshold
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, fontFamily: 'monospace', color: theme.palette.primary.main }}
                      >
                        {thresholdDraft}%
                      </Typography>
                    </Box>
                    <Slider
                      value={thresholdDraft}
                      onChange={(_, v) => {
                        setThresholdDraft(v as number);
                        setThresholdSaved(false);
                      }}
                      min={30}
                      max={95}
                      step={5}
                      marks
                      valueLabelDisplay="auto"
                      valueLabelFormat={(v) => `${v}%`}
                      sx={{
                        color: theme.palette.primary.main,
                        '& .MuiSlider-thumb': { width: 16, height: 16 },
                        '& .MuiSlider-track': { height: 4 },
                        '& .MuiSlider-rail': { height: 4, backgroundColor: theme.palette.background.tertiary },
                      }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                        Analysts are notified when accuracy drops below this threshold.
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={
                          thresholdSaving ||
                          thresholdSaved ||
                          thresholdDraft === (errorBudget?.threshold ?? 70)
                        }
                        onClick={handleSaveThreshold}
                        startIcon={
                          thresholdSaving ? (
                            <CircularProgress size={12} color="inherit" />
                          ) : thresholdSaved ? (
                            <CheckIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <SaveIcon sx={{ fontSize: 14 }} />
                          )
                        }
                        sx={{
                          height: 28,
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          minWidth: 80,
                          flexShrink: 0,
                          ml: 1.5,
                          backgroundColor: thresholdSaved
                            ? theme.palette.success.main
                            : theme.palette.primary.main,
                          '&:hover': {
                            backgroundColor: thresholdSaved
                              ? theme.palette.success.dark
                              : theme.palette.primary.dark,
                          },
                          transition: 'background-color 0.3s ease',
                        }}
                      >
                        {thresholdSaving ? 'Saving…' : thresholdSaved ? 'Saved' : 'Save'}
                      </Button>
                    </Box>
                  </Box>

                  {/* Sessions summary */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {[
                      { label: 'Total sessions', value: accuracy?.sessions_total ?? 0 },
                      { label: 'With feedback', value: accuracy?.sessions_with_feedback ?? 0 },
                      { label: 'Total decisions', value: accuracy?.total_decisions ?? 0 },
                    ].map((item) => (
                      <Box key={item.label} sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: '1.25rem',
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                            fontFamily: 'monospace',
                            lineHeight: 1.2,
                          }}
                        >
                          {item.value}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                          {item.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* CISO sign-off */}
                  <Box
                    sx={{
                      mt: 1,
                      pt: 2,
                      borderTop: `1px solid ${theme.palette.border.light}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <SectionLabel sx={{ mb: 0.25 }}>CISO Sign-off</SectionLabel>
                        <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.67rem' }}>
                          Formal acknowledgment of the AI error budget threshold
                        </Typography>
                      </Box>
                      {clientId && (
                        <Button
                          size="small"
                          variant={cisoAck?.acknowledged_at ? 'outlined' : 'contained'}
                          startIcon={<AcknowledgeIcon sx={{ fontSize: 13 }} />}
                          onClick={() => setCisoDialogOpen(true)}
                          sx={{
                            height: 26,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            flexShrink: 0,
                            ml: 1.5,
                          }}
                        >
                          {cisoAck?.acknowledged_at ? 'Re-acknowledge' : 'Acknowledge'}
                        </Button>
                      )}
                    </Box>
                    {cisoAck?.acknowledged_at ? (
                      <Box sx={{ p: 1.5, borderRadius: 1, backgroundColor: colorUtils.withOpacity(theme.palette.success.main, 0.06), border: `1px solid ${colorUtils.withOpacity(theme.palette.success.main, 0.2)}` }}>
                        <Typography variant="caption" sx={{ display: 'block', color: theme.palette.success.main, fontWeight: 600, fontSize: '0.72rem', mb: 0.25 }}>
                          Acknowledged by {cisoAck.acknowledged_by}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.67rem' }}>
                          {new Date(cisoAck.acknowledged_at).toLocaleString()} · threshold {cisoAck.threshold}%
                        </Typography>
                        {cisoAck.acknowledged_note && (
                          <Typography variant="caption" sx={{ display: 'block', color: theme.palette.text.secondary, fontSize: '0.72rem', mt: 0.5, fontStyle: 'italic' }}>
                            "{cisoAck.acknowledged_note}"
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ p: 1.5, borderRadius: 1, backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.06), border: `1px solid ${colorUtils.withOpacity(theme.palette.warning.main, 0.2)}` }}>
                        <Typography variant="caption" sx={{ color: theme.palette.warning.main, fontWeight: 600, fontSize: '0.72rem' }}>
                          No CISO acknowledgment on record
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </>
              ) : (
                <EmptyCard />
              )}
            </Box>
          </Grid>
        </Grid>

        {/* ── Historical Snapshots ─────────────────────────────────────────── */}
        <Box sx={{ mt: 2.5 }}>
          <Box sx={cardSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <SectionLabel>Historical AI Performance — Weekly Snapshots</SectionLabel>
                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                  Materialized weekly snapshots — acceptance rate and false positive rate over time
                </Typography>
              </Box>
              {snapshots.length > 0 && (
                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                  {snapshots.length} week{snapshots.length !== 1 ? 's' : ''}
                </Typography>
              )}
            </Box>

            {snapshotsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : snapshots.length > 0 ? (
              <>
                <LineChart
                  data={snapshots.map((s) => ({
                    name: s.week_start.slice(5), // MM-DD
                    'Acceptance Rate': s.acceptance_rate ?? 0,
                    'FP Rate': s.false_positive_rate ?? 0,
                  }))}
                  height={200}
                  variant="line"
                  curved
                  showLegend
                  dataKeys={[
                    { key: 'Acceptance Rate', label: 'Acceptance Rate (%)', color: 'oklch(65% 0.18 150)' },
                    { key: 'FP Rate', label: 'False Positive Rate (%)', color: 'oklch(62% 0.22 25)' },
                  ]}
                />

                {/* Summary table of last 4 weeks */}
                <Box sx={{ mt: 2.5, overflowX: 'auto' }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, 1fr)',
                      gap: 0,
                      minWidth: 600,
                    }}
                  >
                    {['Week', 'Acceptance', 'FP Rate', 'MTTR AI', 'MTTR Manual', 'Decisions', 'Resolved'].map((h) => (
                      <Typography
                        key={h}
                        variant="caption"
                        sx={{
                          color: theme.palette.text.tertiary,
                          fontWeight: 700,
                          fontSize: '0.63rem',
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase',
                          py: 0.75,
                          px: 1,
                          borderBottom: `1px solid ${theme.palette.border.light}`,
                        }}
                      >
                        {h}
                      </Typography>
                    ))}
                    {snapshots.slice(-8).reverse().map((s) => {
                      const cells = [
                        s.week_start,
                        s.acceptance_rate != null ? `${s.acceptance_rate}%` : '—',
                        s.false_positive_rate != null ? `${s.false_positive_rate}%` : '—',
                        s.ai_assisted_mttr_hours != null ? `${s.ai_assisted_mttr_hours}h` : '—',
                        s.manual_mttr_hours != null ? `${s.manual_mttr_hours}h` : '—',
                        String(s.total_decisions),
                        String(s.total_resolved_tickets),
                      ];
                      return cells.map((val, i) => (
                        <Typography
                          key={`${s.id}-${i}`}
                          variant="caption"
                          sx={{
                            fontSize: '0.75rem',
                            fontFamily: i > 0 ? 'monospace' : 'inherit',
                            fontWeight: i === 0 ? 500 : 400,
                            color: theme.palette.text.primary,
                            py: 1,
                            px: 1,
                            borderBottom: `1px solid ${theme.palette.border.light}`,
                          }}
                        >
                          {val}
                        </Typography>
                      ));
                    })}
                  </Box>
                </Box>
              </>
            ) : (
              <EmptyCard loading={snapshotsLoading} />
            )}
          </Box>
        </Box>

        {/* ── MTTD Section ──────────────────────────────────────────────── */}
        <Box sx={{ mt: 2.5 }}>
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={cardSx}>
                <Box sx={{ mb: 2 }}>
                  <SectionLabel>MTTD — Mean Time to Detect</SectionLabel>
                  <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                    Time from vendor detection event to ticket creation, by week
                  </Typography>
                </Box>
                {mttdLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
                ) : mttd && mttd.weekly_trend.length > 0 ? (
                  <LineChart
                    data={mttd.weekly_trend.map((b) => ({ name: b.week.slice(5), 'MTTD (h)': b.avg_hours ?? 0 }))}
                    height={180}
                    variant="area"
                    curved
                    showLegend={false}
                    dataKeys={[{ key: 'MTTD (h)', label: 'MTTD (hours)', color: 'oklch(60% 0.22 291)' }]}
                  />
                ) : (
                  <EmptyCard />
                )}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={cardSx}>
                <SectionLabel>MTTD — Auto vs Manual ticketing</SectionLabel>
                {mttdLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
                ) : mttd ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {[
                      { label: 'Overall avg', value: mttd.overall_avg_hours != null ? `${mttd.overall_avg_hours}h` : '—', sub: `${mttd.total_with_detection_time} tickets with detection time` },
                      { label: 'Auto-ticketed avg', value: mttd.auto_ticketed_avg_hours != null ? `${mttd.auto_ticketed_avg_hours}h` : '—', sub: '' },
                      { label: 'Manual-ticketed avg', value: mttd.manual_ticketed_avg_hours != null ? `${mttd.manual_ticketed_avg_hours}h` : '—', sub: '' },
                    ].map(({ label, value, sub }) => (
                      <Box key={label}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>{label}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace', color: theme.palette.text.primary }}>{value}</Typography>
                        </Box>
                        {sub && <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.67rem' }}>{sub}</Typography>}
                      </Box>
                    ))}
                    {mttd.by_priority.length > 0 && (
                      <Box sx={{ mt: 0.5, pt: 1.5, borderTop: `1px solid ${theme.palette.border.light}` }}>
                        <SectionLabel>By priority</SectionLabel>
                        {mttd.by_priority.map((p) => (
                          <Box key={p.priority} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'capitalize' }}>{p.priority}</Typography>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: theme.palette.text.primary }}>
                              {p.avg_hours != null ? `${p.avg_hours}h` : '—'} ({p.count})
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <EmptyCard />
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* ── Data Quality ─────────────────────────────────────────────── */}
        <Box sx={{ mt: 2.5 }}>
          <Box sx={cardSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <DataQualityIcon sx={{ fontSize: 16, color: theme.palette.info.main }} />
              <SectionLabel sx={{ mb: 0 }}>Data Quality — Structured data completeness</SectionLabel>
            </Box>
            <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, display: 'block', mb: 2 }}>
              % of resolved tickets with root_cause and MITRE tags — required for AI training fuel
            </Typography>
            {dataQualityLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
            ) : dataQuality ? (
              <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                    {[
                      { label: 'Root cause rate', value: dataQuality.resolved_tickets.root_cause_rate, color: theme.palette.success.main },
                      { label: 'MITRE tags rate', value: dataQuality.resolved_tickets.mitre_tags_rate, color: theme.palette.info.main },
                      { label: 'Fully complete', value: dataQuality.resolved_tickets.fully_complete_rate, color: theme.palette.primary.main },
                    ].map(({ label, value, color }) => (
                      <Box key={label}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>{label}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace', color }}>{value != null ? `${Math.round(value * 100)}%` : '—'}</Typography>
                        </Box>
                        <Box sx={{ height: 5, borderRadius: 3, backgroundColor: theme.palette.background.tertiary, overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: `${Math.round((value ?? 0) * 100)}%`, borderRadius: 3, backgroundColor: color, transition: 'width 0.4s ease' }} />
                        </Box>
                      </Box>
                    ))}
                    <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                      {dataQuality.resolved_tickets.total} resolved · {dataQuality.resolved_tickets.incomplete_count} incomplete
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                  {dataQuality.weekly_trend.length > 0 ? (
                    <LineChart
                      data={dataQuality.weekly_trend.map((b) => ({
                        name: b.week.slice(5),
                        'Root cause': Math.round((b.root_cause_rate ?? 0) * 100),
                        'MITRE tags': Math.round((b.mitre_tags_rate ?? 0) * 100),
                      }))}
                      height={160}
                      variant="line"
                      curved
                      showLegend
                      dataKeys={[
                        { key: 'Root cause', label: 'Root cause (%)', color: 'oklch(65% 0.18 150)' },
                        { key: 'MITRE tags', label: 'MITRE tags (%)', color: 'oklch(65% 0.18 220)' },
                      ]}
                    />
                  ) : <EmptyCard />}
                </Grid>
              </Grid>
            ) : (
              <EmptyCard />
            )}
          </Box>
        </Box>

        {/* ── Agent Uptime ─────────────────────────────────────────────── */}
        <Box sx={{ mt: 2.5 }}>
          <Box sx={cardSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <AgentIcon sx={{ fontSize: 16, color: agentUptime
                  ? agentUptime.status === 'healthy' ? theme.palette.success.main
                  : agentUptime.status === 'degraded' ? theme.palette.warning.main
                  : theme.palette.error.main
                  : theme.palette.text.tertiary }} />
                <SectionLabel sx={{ mb: 0 }}>Agent Uptime — LLM endpoint health</SectionLabel>
              </Box>
              {agentUptime && (
                <Chip
                  label={agentUptime.status.toUpperCase()}
                  size="small"
                  sx={{
                    height: 20, fontSize: '0.62rem', fontWeight: 700,
                    color: agentUptime.status === 'healthy' ? theme.palette.success.main : agentUptime.status === 'degraded' ? theme.palette.warning.main : agentUptime.status === 'down' ? theme.palette.error.main : theme.palette.text.disabled,
                    backgroundColor: colorUtils.withOpacity(agentUptime.status === 'healthy' ? theme.palette.success.main : agentUptime.status === 'degraded' ? theme.palette.warning.main : agentUptime.status === 'down' ? theme.palette.error.main : theme.palette.text.disabled, 0.1),
                    border: `1px solid ${colorUtils.withOpacity(agentUptime.status === 'healthy' ? theme.palette.success.main : agentUptime.status === 'degraded' ? theme.palette.warning.main : theme.palette.error.main, 0.3)}`,
                  }}
                />
              )}
            </Box>
            {agentUptimeLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
            ) : agentUptime ? (
              <Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {[
                    { label: '1h uptime', value: agentUptime.uptime_1h_pct != null ? `${agentUptime.uptime_1h_pct}%` : '—', sub: `${agentUptime.successful_1h}/${agentUptime.total_calls_1h} calls` },
                    { label: '24h uptime', value: agentUptime.uptime_24h_pct != null ? `${agentUptime.uptime_24h_pct}%` : '—', sub: `${agentUptime.successful_24h}/${agentUptime.total_calls_24h} calls` },
                    { label: 'Avg duration', value: agentUptime.avg_duration_s != null ? `${agentUptime.avg_duration_s}s` : '—', sub: 'per generation' },
                  ].map(({ label, value, sub }) => (
                    <Grid key={label} size={{ xs: 4 }}>
                      <Box>
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, fontFamily: 'monospace', color: theme.palette.text.primary, lineHeight: 1.2 }}>{value}</Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.65rem' }}>{label}</Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.62rem', display: 'block' }}>{sub}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                {agentUptime.last_failure_at && (
                  <Box sx={{ p: 1.25, borderRadius: 1, backgroundColor: colorUtils.withOpacity(theme.palette.error.main, 0.06), border: `1px solid ${colorUtils.withOpacity(theme.palette.error.main, 0.2)}` }}>
                    <Typography variant="caption" sx={{ color: theme.palette.error.main, fontWeight: 600, fontSize: '0.7rem', display: 'block' }}>
                      Last failure: {agentUptime.last_failure_at}
                    </Typography>
                    {agentUptime.last_error && (
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem', fontFamily: 'monospace' }}>{agentUptime.last_error}</Typography>
                    )}
                  </Box>
                )}
                <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.65rem', display: 'block', mt: 1 }}>
                  Model: {agentUptime.llm_model} · {agentUptime.llm_url}
                </Typography>
              </Box>
            ) : (
              <EmptyCard />
            )}
          </Box>
        </Box>

        {/* ── Pre-AI Baseline Comparison ──────────────────────────────── */}
        <Box sx={{ mt: 2.5 }}>
          <Box sx={cardSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                  <BaselineIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                  <SectionLabel sx={{ mb: 0 }}>Pre-AI Baseline Comparison</SectionLabel>
                </Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                  Declared pre-AI baseline vs current metrics — the "did AI help?" proof
                </Typography>
              </Box>
              {clientId && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                  onClick={() => setBaselineDialogOpen(true)}
                  sx={{ height: 32, fontSize: '0.75rem', fontWeight: 600, borderColor: theme.palette.border.main, color: theme.palette.text.secondary, '&:hover': { borderColor: theme.palette.primary.main, color: theme.palette.primary.main } }}
                >
                  {baseline?.has_baseline ? 'Update baseline' : 'Declare baseline'}
                </Button>
              )}
            </Box>
            {baselineLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
            ) : !baseline || !baseline.has_baseline ? (
              <Box sx={{ p: 2.5, borderRadius: 1.5, backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.06), border: `1px solid ${colorUtils.withOpacity(theme.palette.warning.main, 0.2)}`, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: theme.palette.warning.main, fontWeight: 600, mb: 0.5 }}>No baseline declared</Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                  Declare a pre-AI baseline period to enable before/after comparison. Once declared, the baseline is locked.
                </Typography>
              </Box>
            ) : (
              <Box>
                {baseline.baseline_meta && (
                  <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, display: 'block', mb: 2 }}>
                    Baseline: {baseline.baseline_meta.period_start} → {baseline.baseline_meta.period_end} · declared by {baseline.baseline_meta.declared_by} on {new Date(baseline.baseline_meta.declared_at).toLocaleDateString()}
                  </Typography>
                )}
                <Box sx={{ overflowX: 'auto' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 0, minWidth: 480 }}>
                    {['Metric', 'Before AI', 'Current', 'Delta'].map((h) => (
                      <Typography key={h} variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, fontSize: '0.63rem', letterSpacing: '0.05em', textTransform: 'uppercase', py: 0.75, px: 1, borderBottom: `1px solid ${theme.palette.border.light}` }}>{h}</Typography>
                    ))}
                    {[
                      { label: 'MTTR avg (h)', before: baseline.before?.avg_mttr_hours != null ? `${baseline.before.avg_mttr_hours}h` : '—', current: baseline.current?.avg_mttr_hours != null ? `${baseline.current.avg_mttr_hours}h` : '—', delta: baseline.deltas?.mttr_pct_change, lowerIsBetter: true },
                      { label: 'MTTD avg (h)', before: baseline.before?.avg_mttd_hours != null ? `${baseline.before.avg_mttd_hours}h` : '—', current: baseline.current?.avg_mttd_hours != null ? `${baseline.current.avg_mttd_hours}h` : '—', delta: baseline.deltas?.mttd_pct_change, lowerIsBetter: true },
                      { label: 'Root cause rate', before: baseline.before?.root_cause_rate != null ? `${Math.round(baseline.before.root_cause_rate * 100)}%` : '—', current: baseline.current?.root_cause_rate != null ? `${Math.round(baseline.current.root_cause_rate * 100)}%` : '—', delta: null, lowerIsBetter: false },
                      { label: 'MITRE tags rate', before: baseline.before?.mitre_tags_rate != null ? `${Math.round(baseline.before.mitre_tags_rate * 100)}%` : '—', current: baseline.current?.mitre_tags_rate != null ? `${Math.round(baseline.current.mitre_tags_rate * 100)}%` : '—', delta: null, lowerIsBetter: false },
                    ].map(({ label, before, current, delta, lowerIsBetter }) => {
                      const improved = delta != null && (lowerIsBetter ? delta < 0 : delta > 0);
                      const worsened = delta != null && (lowerIsBetter ? delta > 0 : delta < 0);
                      const deltaColor = improved ? theme.palette.success.main : worsened ? theme.palette.error.main : theme.palette.text.secondary;
                      return [label, before, current, delta != null ? `${delta > 0 ? '+' : ''}${delta}%` : '—'].map((val, i) => (
                        <Typography key={`${label}-${i}`} variant="caption" sx={{ fontSize: '0.78rem', fontFamily: i > 0 ? 'monospace' : 'inherit', fontWeight: i === 0 ? 500 : 600, color: i === 3 ? deltaColor : theme.palette.text.primary, py: 1, px: 1, borderBottom: `1px solid ${theme.palette.border.light}` }}>
                          {val}
                        </Typography>
                      ));
                    })}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

      </Box>

      {/* ── Declare Baseline Dialog ──────────────────────────────────────── */}
      <Dialog open={baselineDialogOpen} onClose={() => setBaselineDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundColor: theme.palette.background.paper, backgroundImage: 'none' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>Declare Pre-AI Baseline</Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, display: 'block', mt: 0.25 }}>
            Once declared, the baseline is locked and serves as your permanent "before AI" reference.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Period start" type="date" value={baselineStart} onChange={(e) => setBaselineStart(e.target.value)} size="small" fullWidth InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { backgroundColor: theme.palette.background.tertiary, '& fieldset': { borderColor: theme.palette.border.main } } }} />
            <TextField label="Period end" type="date" value={baselineEnd} onChange={(e) => setBaselineEnd(e.target.value)} size="small" fullWidth InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { backgroundColor: theme.palette.background.tertiary, '& fieldset': { borderColor: theme.palette.border.main } } }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <input type="checkbox" id="manual-mode" checked={baselineManual} onChange={(e) => setBaselineManual(e.target.checked)} />
            <Typography component="label" htmlFor="manual-mode" variant="caption" sx={{ color: theme.palette.text.secondary, cursor: 'pointer' }}>Enter metrics manually (historical data not in system)</Typography>
          </Box>
          {baselineManual && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Avg MTTR (hours)" value={baselineManualMttr} onChange={(e) => setBaselineManualMttr(e.target.value)} type="number" size="small" fullWidth sx={{ '& .MuiOutlinedInput-root': { backgroundColor: theme.palette.background.tertiary, '& fieldset': { borderColor: theme.palette.border.main } } }} />
              <TextField label="Avg MTTD (hours)" value={baselineManualMttd} onChange={(e) => setBaselineManualMttd(e.target.value)} type="number" size="small" fullWidth sx={{ '& .MuiOutlinedInput-root': { backgroundColor: theme.palette.background.tertiary, '& fieldset': { borderColor: theme.palette.border.main } } }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setBaselineDialogOpen(false)} size="small" sx={{ color: theme.palette.text.secondary }}>Cancel</Button>
          <Button variant="contained" size="small" disabled={!baselineStart || !baselineEnd || baselineSaving} onClick={handleDeclareBaseline} startIcon={baselineSaving ? <CircularProgress size={13} color="inherit" /> : <SaveIcon sx={{ fontSize: 14 }} />} sx={{ fontWeight: 600, px: 2 }}>
            {baselineSaving ? 'Declaring…' : 'Declare baseline'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── CISO Acknowledge Dialog ──────────────────────────────────────── */}
      <Dialog open={cisoDialogOpen} onClose={() => setCisoDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { backgroundColor: theme.palette.background.paper, backgroundImage: 'none' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>Acknowledge Error Budget</Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, display: 'block', mt: 0.25 }}>
            Threshold: {errorBudget?.threshold ?? '—'}% · Your identity is recorded from your login session.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField label="Note (optional)" multiline minRows={2} fullWidth value={cisoNote} onChange={(e) => setCisoNote(e.target.value)} placeholder="Reason or context for this acknowledgment" size="small"
            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: theme.palette.background.tertiary, '& fieldset': { borderColor: theme.palette.border.main } } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setCisoDialogOpen(false)} size="small" sx={{ color: theme.palette.text.secondary }}>Cancel</Button>
          <Button variant="contained" size="small" disabled={cisoSaving} onClick={handleCISOAcknowledge} startIcon={cisoSaving ? <CircularProgress size={13} color="inherit" /> : <AcknowledgeIcon sx={{ fontSize: 14 }} />}
            sx={{ fontWeight: 600, px: 2, backgroundColor: theme.palette.success.main, '&:hover': { backgroundColor: theme.palette.success.dark } }}>
            {cisoSaving ? 'Acknowledging…' : 'Acknowledge'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default AIPerformancePage;
